import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, updateUserProfile, changePassword, getEmailPreferences, updateEmailPreferences } from '../utils/api';
import HourSelect from '../components/HourSelect';
// User settings page with profile and email preferences
import './UserSettings.css';

function UserSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile state
  const [profile, setProfile] = useState({
    username: '',
    full_name: ''
  });

  // Password state
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Email preferences state
  const [emailPrefs, setEmailPrefs] = useState({
    email_daily_summary: false,
    email_daily_time: '08:00',
    email_weekly_summary: false,
    email_weekly_day: 1,
    email_weekly_time: '08:00'
  });

  // Email digest access state
  const [emailDigestAllowed, setEmailDigestAllowed] = useState(true);

  const weekDays = [
    { value: 0, label: 'Søndag' },
    { value: 1, label: 'Mandag' },
    { value: 2, label: 'Tirsdag' },
    { value: 3, label: 'Onsdag' },
    { value: 4, label: 'Torsdag' },
    { value: 5, label: 'Fredag' },
    { value: 6, label: 'Lørdag' }
  ];

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      // Load current user info
      const userData = await getCurrentUser(token);
      setProfile({
        username: userData.username,
        full_name: userData.fullName
      });

      // Load email preferences
      try {
        const prefs = await getEmailPreferences(token);
        setEmailPrefs({
          email_daily_summary: Boolean(prefs.email_daily_summary),
          email_daily_time: prefs.email_daily_time || '08:00',
          email_weekly_summary: Boolean(prefs.email_weekly_summary),
          email_weekly_day: prefs.email_weekly_day !== undefined ? prefs.email_weekly_day : 1,
          email_weekly_time: prefs.email_weekly_time || '08:00'
        });
        setEmailDigestAllowed(true);
      } catch (err) {
        // If we get a 403, email digest is not allowed for this user - this is normal
        if (err.status === 403) {
          setEmailDigestAllowed(false);
          // Don't show error, just hide the email section
        } else {
          throw err;
        }
      }

      setLoading(false);
    } catch (err) {
      setError('Kunne ikke laste brukerdata');
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await updateUserProfile(token, profile);
      setSuccess('Profil oppdatert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Kunne ikke oppdatere profil');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('Nye passord matcher ikke');
      return;
    }

    if (passwords.newPassword.length < 6) {
      setError('Nytt passord må være minst 6 tegn');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await changePassword(token, passwords.currentPassword, passwords.newPassword);
      setSuccess('Passord endret!');
      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Kunne ikke endre passord');
    }
  };

  const handleEmailPrefsUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await updateEmailPreferences(token, emailPrefs);
      setSuccess('E-postinnstillinger oppdatert!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Kunne ikke oppdatere e-postinnstillinger');
    }
  };

  if (loading) {
    return <div className="settings-loading">Laster...</div>;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Innstillinger</h1>
        <button onClick={() => navigate('/reports')} className="back-button">
          ← Tilbake til rapporter
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Profile Section */}
      <div className="settings-section">
        <h2>👤 Profil</h2>
        <form onSubmit={handleProfileUpdate}>
          <div className="form-group">
            <label>Brukernavn</label>
            <input
              type="text"
              value={profile.username}
              onChange={(e) => setProfile({ ...profile, username: e.target.value })}
              required
            />
            {emailDigestAllowed && <small>Må være en e-postadresse for å motta e-postvarsler</small>}
          </div>

          <div className="form-group">
            <label>Fullt navn</label>
            <input
              type="text"
              value={profile.full_name}
              onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
              required
            />
          </div>

          <button type="submit" className="btn-primary">Oppdater profil</button>
        </form>
      </div>

      {/* Password Section */}
      <div className="settings-section">
        <h2>🔒 Endre passord</h2>
        <form onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label>Nåværende passord</label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Nytt passord</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Bekreft nytt passord</label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              required
            />
          </div>

          <button type="submit" className="btn-primary">Endre passord</button>
        </form>
      </div>

      {/* Email Preferences Section */}
      {emailDigestAllowed && (
        <div className="settings-section">
          <h2>📧 E-postvarsler</h2>
          <form onSubmit={handleEmailPrefsUpdate}>
            <div className="email-section">
              <h3>Daglig sammendrag</h3>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={emailPrefs.email_daily_summary}
                    onChange={(e) => setEmailPrefs({ ...emailPrefs, email_daily_summary: e.target.checked })}
                  />
                  Aktiver daglig e-post med rapporter fra siste 24 timer
                </label>
                <span className={`status-badge ${emailPrefs.email_daily_summary ? 'active' : 'inactive'}`}>
                  {emailPrefs.email_daily_summary ? 'Aktivert' : 'Deaktivert'}
                </span>
              </div>

              <div className="form-group">
                <label>Time for daglig e-post</label>
                <HourSelect
                  value={emailPrefs.email_daily_time}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, email_daily_time: e.target.value })}
                  disabled={!emailPrefs.email_daily_summary}
                />
                <small>E-posten sendes på hele timer (f.eks. 08:00, 17:00)</small>
              </div>
            </div>

            <div className="email-section">
              <h3>Ukentlig sammendrag</h3>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={emailPrefs.email_weekly_summary}
                    onChange={(e) => setEmailPrefs({ ...emailPrefs, email_weekly_summary: e.target.checked })}
                  />
                  Aktiver ukentlig e-post med rapporter fra siste 7 dager
                </label>
                <span className={`status-badge ${emailPrefs.email_weekly_summary ? 'active' : 'inactive'}`}>
                  {emailPrefs.email_weekly_summary ? 'Aktivert' : 'Deaktivert'}
                </span>
              </div>

              <div className="form-group">
                <label>Dag for ukentlig e-post</label>
                <select
                  value={emailPrefs.email_weekly_day}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, email_weekly_day: parseInt(e.target.value) })}
                  disabled={!emailPrefs.email_weekly_summary}
                >
                  {weekDays.map(day => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Time for ukentlig e-post</label>
                <HourSelect
                  value={emailPrefs.email_weekly_time}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, email_weekly_time: e.target.value })}
                  disabled={!emailPrefs.email_weekly_summary}
                />
                <small>E-posten sendes på hele timer (f.eks. 08:00, 17:00)</small>
              </div>
            </div>

            <div className="info-box">
              <strong>ℹ️ Viktig:</strong> Brukernavnet ditt må være en gyldig e-postadresse for å motta e-postvarsler.
            </div>

            <button type="submit" className="btn-primary">Lagre e-postinnstillinger</button>
          </form>
        </div>
      )}
    </div>
  );
}

export default UserSettings;
